import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isTrustedDevice } from '../../utils/deviceFingerprint';
import { Box, Button, TextField, Typography, Paper, InputAdornment, IconButton, Alert, Container, CircularProgress, keyframes, Tabs, Tab } from '@mui/material';
import { LockIcon, UserIcon, EyeIcon, EyeOffIcon, HomeIcon } from 'lucide-react';
// Define keyframes for animations
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;
const LoginPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [branchCode, setBranchCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showCapsLock, setShowCapsLock] = useState(false);
  const [errors, setErrors] = useState<{
    userId?: string;
    password?: string;
    branchCode?: string;
    general?: string;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const {
    isAuthenticated,
    login
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const message = location.state?.message || '';
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/');
    }
  }, [isAuthenticated, navigate, isLoading]);
  useEffect(() => {
    const userIdError = validateUserId(userId);
    const passwordError = validatePassword(password);
    const branchCodeError = activeTab === 0 ? validateBranchCode(branchCode) : '';
    setFormValid(!userIdError && !passwordError && !branchCodeError);
  }, [userId, password, branchCode, activeTab]);
  const validateUserId = (value: string) => {
    if (!value) return 'Username is required';
    return '';
  };
  const validatePassword = (value: string) => {
    if (!value) return 'Password is required';
    const minLength = value.length >= 8;
    const maxLength = value.length <= 15;
    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);
    if (!minLength || !maxLength) {
      return 'Password must be between 8-15 characters';
    }
    if (!hasUppercase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowercase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumber) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecial) {
      return 'Password must contain at least one special character';
    }
    return '';
  };
  const validateBranchCode = (value: string) => {
    if (!value) return 'Branch code is required';
    if (!/^\d{4}$/.test(value)) {
      return 'Branch code must be exactly 4 digits';
    }
    return '';
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const userIdError = validateUserId(userId);
    const passwordError = validatePassword(password);
    const branchCodeError = activeTab === 0 ? validateBranchCode(branchCode) : '';
    if (userIdError || passwordError || branchCodeError) {
      setErrors({
        userId: userIdError,
        password: passwordError,
        branchCode: branchCodeError
      });
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    setIsLoading(true);
    try {
      const success = await login(userId, password);
      if (success) {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          // Get the ACTUAL user data from users collection to check firstLoginRequired
          const storedUsers = localStorage.getItem('users');
          let actualFirstLoginRequired = userData.firstLoginRequired;
          if (storedUsers) {
            const users = JSON.parse(storedUsers);
            const actualUser = users.find((u: any) => u.id === userData.id);
            if (actualUser) {
              actualFirstLoginRequired = actualUser.firstLoginRequired || false;
              // Update userData with actual firstLoginRequired status
              userData.firstLoginRequired = actualFirstLoginRequired;
            }
          }
          // Set branch code and user type based on login tab
          if (activeTab === 0) {
            userData.branchCode = branchCode;
            userData.userType = 'branch';
            userData.isAgent = false;
            userData.agentCode = 'BIPL';
          } else {
            const randomBranchCode = String(Math.floor(1000 + Math.random() * 9000));
            userData.branchCode = randomBranchCode;
            userData.userType = 'agent';
            userData.isAgent = true;
            const agentCodes = ['HBL', 'MCB', 'FBL', 'ABL', 'UBL'];
            const randomAgentCode = agentCodes[Math.floor(Math.random() * agentCodes.length)];
            userData.agentCode = randomAgentCode;
          }
          localStorage.setItem('user', JSON.stringify(userData));
          // Check if this is a trusted device
          const isTrusted = isTrustedDevice(userData.id);
          console.log('Login Debug:', {
            userId: userData.id,
            firstLoginRequired: actualFirstLoginRequired,
            isTrusted,
            trustedDevices: localStorage.getItem('trustedDevices')
          });
          // FIRST-TIME LOGIN FLOW
          if (actualFirstLoginRequired) {
            // First time login - always go to OTP, then password change
            navigate('/otp-verification', {
              state: {
                userData
              }
            });
          } else if (isTrusted) {
            // Not first time, trusted device - go directly to dashboard
            if (userData.role === 'checker') {
              navigate('/transaction-approval');
            } else {
              navigate('/');
            }
          } else {
            // Not first time, but not trusted device - require OTP
            navigate('/otp-verification', {
              state: {
                userData
              }
            });
          }
        }
      } else {
        setErrors({
          general: 'Invalid credentials. Please check your username and password.'
        });
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
      }
    } catch (error) {
      setErrors({
        general: 'An error occurred. Please try again later.'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.getModifierState('CapsLock')) {
      setShowCapsLock(true);
    } else {
      setShowCapsLock(false);
    }
  };
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setErrors({});
    if (newValue === 1) {
      setBranchCode('');
    }
  };
  return <Box sx={{
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden'
  }} data-id="element-177">
      {/* Left Side - Branding (40%) */}
      <Box sx={{
      display: {
        xs: 'none',
        md: 'flex'
      },
      width: '40%',
      backgroundColor: '#0052CC',
      flexDirection: 'column',
      justifyContent: 'space-between',
      p: 6,
      position: 'relative',
      overflow: 'hidden'
    }} data-id="element-178">
        {/* Geometric Pattern Background */}
        <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.1,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        zIndex: 0
      }} data-id="element-179" />

        <Box sx={{
        position: 'relative',
        zIndex: 1
      }} data-id="element-180">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xyh4FinweYvNRM6XVnTFSv/openRemitLogo-1.png" alt="OpenRemit Logo" style={{
          height: 48,
          marginBottom: 40,
          filter: 'brightness(0) invert(1)' // Make logo white
        }} data-id="element-181" />
          <Typography variant="h3" sx={{
          color: '#FFFFFF',
          fontWeight: 700,
          mb: 2,
          lineHeight: 1.2
        }} data-id="element-182">
            Enterprise
            <br data-id="element-183" />
            Banking Portal
          </Typography>
          <Typography variant="body1" sx={{
          color: 'rgba(255, 255, 255, 0.8)',
          maxWidth: 320,
          lineHeight: 1.6
        }} data-id="element-184">
            Secure, efficient, and comprehensive remittance management for
            branch and agent networks.
          </Typography>
        </Box>

        <Box sx={{
        position: 'relative',
        zIndex: 1
      }} data-id="element-185">
          <Typography variant="caption" sx={{
          color: 'rgba(255, 255, 255, 0.6)'
        }} data-id="element-186">
            © 2026 OpenRemit. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* Right Side - Login Form (60%) */}
      <Box sx={{
      width: {
        xs: '100%',
        md: '60%'
      },
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      p: {
        xs: 3,
        sm: 6
      },
      position: 'relative'
    }} data-id="element-187">
        <Box sx={{
        width: '100%',
        maxWidth: 400
      }} data-id="element-188">
          {/* Mobile Logo */}
          <Box sx={{
          display: {
            xs: 'flex',
            md: 'none'
          },
          justifyContent: 'center',
          mb: 4
        }} data-id="element-189">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xyh4FinweYvNRM6XVnTFSv/openRemitLogo-1.png" alt="OpenRemit Logo" style={{
            height: 40
          }} data-id="element-190" />
          </Box>

          <Typography variant="h4" component="h1" sx={{
          mb: 1,
          color: '#1F2937',
          fontWeight: 700
        }} data-id="element-191">
            Sign In
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{
          mb: 4
        }} data-id="element-192">
            Welcome back! Please enter your details.
          </Typography>

          {message && <Alert severity="success" sx={{
          mb: 3
        }} data-id="element-193">
              {message}
            </Alert>}

          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            color: '#6B7280',
            '&.Mui-selected': {
              color: '#0052CC'
            }
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#0052CC'
          }
        }} data-id="element-194">
            <Tab label="Branch User" data-id="element-195" />
            <Tab label="Agent" data-id="element-196" />
          </Tabs>

          {errors.general && <Alert severity="error" sx={{
          mb: 3
        }} data-id="element-197">
              {errors.general}
            </Alert>}

          <Alert severity="info" sx={{
          mb: 3
        }} data-id="element-198">
            <Typography variant="caption" data-id="element-199">
              Demo Credentials: Username: <strong data-id="element-200">branchmaker</strong> |
              Password: <strong data-id="element-201">Test@1234</strong>
            </Typography>
          </Alert>

          <form onSubmit={handleSubmit} data-id="element-202">
            <TextField id="userId" label="User ID" variant="outlined" fullWidth value={userId} onChange={e => setUserId(e.target.value)} error={!!errors.userId} helperText={errors.userId} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-204">
                    <UserIcon size={20} color="#6B7280" data-id="element-205" />
                  </InputAdornment>
          }} sx={{
            mb: 2.5
          }} data-id="element-203" />

            <TextField id="password" label="Password" type={showPassword ? 'text' : 'password'} variant="outlined" fullWidth value={password} onChange={e => setPassword(e.target.value)} onKeyDown={handleKeyDown} error={!!errors.password} helperText={errors.password || (showCapsLock ? 'Caps lock is on' : '')} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-207">
                    <LockIcon size={20} color="#6B7280" data-id="element-208" />
                  </InputAdornment>,
            endAdornment: <InputAdornment position="end" data-id="element-209">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{
                color: '#6B7280'
              }} data-id="element-210">
                      {showPassword ? <EyeOffIcon size={20} data-id="element-211" /> : <EyeIcon size={20} data-id="element-212" />}
                    </IconButton>
                  </InputAdornment>
          }} sx={{
            mb: activeTab === 0 ? 2.5 : 3
          }} data-id="element-206" />

            {activeTab === 0 && <TextField id="branchCode" label="Branch Code" variant="outlined" fullWidth value={branchCode} onChange={e => {
            const value = e.target.value.replace(/\D/g, '');
            if (value.length <= 4) setBranchCode(value);
          }} error={!!errors.branchCode} helperText={errors.branchCode || '4-digit branch code (e.g., 0001)'} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-214">
                      <HomeIcon size={20} color="#6B7280" data-id="element-215" />
                    </InputAdornment>
          }} inputProps={{
            maxLength: 4,
            pattern: '[0-9]{4}'
          }} sx={{
            mb: 3
          }} data-id="element-213" />}

            <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 3
          }} data-id="element-216">
              <Typography component={RouterLink} to="/forgot-password" variant="body2" sx={{
              color: '#0052CC',
              textDecoration: 'none',
              fontWeight: 600,
              '&:hover': {
                textDecoration: 'underline'
              }
            }} data-id="element-217">
                Forgot password?
              </Typography>
            </Box>

            <Button type="submit" variant="contained" fullWidth disabled={isLoading || !formValid} sx={{
            py: 1.5,
            mb: 4,
            borderRadius: '6px',
            backgroundColor: '#0052CC',
            fontSize: '1rem',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#003DA8',
              boxShadow: '0px 1px 3px rgba(0,0,0,0.12)'
            }
          }} data-id="element-218">
              {isLoading ? <CircularProgress size={24} sx={{
              color: 'white'
            }} data-id="element-219" /> : 'Sign In'}
            </Button>
          </form>
        </Box>

        {/* Footer */}
        <Box sx={{
        position: 'absolute',
        bottom: 24,
        width: '100%',
        textAlign: 'center'
      }} data-id="element-220">
          <Typography variant="caption" sx={{
          color: '#9CA3AF',
          fontWeight: 500
        }} data-id="element-221">
            Powered by Paysys Labs
          </Typography>
        </Box>
      </Box>
    </Box>;
};
export default LoginPage;